<template>
  <div class="short-answer">
    <!-- Instructions -->
    <p
      :class="{ 'short-answer__instructions--view-mode': mode === 'VIEW' }"
      class="short-answer__instructions"
    >
      {{ mode === 'EDIT'
        ? $t('Answer with short answer')
        : decodeHTML(moduleData.options.content) }}
    </p>

    <!-- Question -->
    <TEIError :validation-error="validationError" />
    <InteractiveModulePrompt
      v-if="mode === 'EDIT'"
      class="short-answer__prompt"
      :canvas-name="canvasName"
      :module-data="clonedModuleData"
      :id="id"
      @update-module="validateAndUpdateTEI"
    />

    <!-- Answer -->
    <StudioTextEditor
      class="short-answer__text-editor"
      :content="answer"
      :disabled="!userCanEditAttemptQuizItems"
      data-type="html"
      mode="EDIT"
      :floating-menu="false"
      :module-active="active"
      :scale="scale"
      :disable-icons="true"
      :id="id"
      :disable-modal-link-option="true"
      :disable-link="true"
      :canvas-name="canvasName"
      @update="submitAnswer"
    />
  </div>
</template>

<script>
import { debounce, get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { decodeHTML } from '@/lib/utils';
import moduleData from '@/mixins/moduleData';
import teiValidation from '@/mixins/teiValidation';
import * as types from '@/lib/constants/store';
import StudioTextEditor from '@/components/StudioTextEditor.vue';
import {
  useAssessmentStore,
} from '@/stores';
import InteractiveModulePrompt from './InteractiveModulePrompt.vue';
import TEIError from './TEIError.vue';

export default {
  name: 'PromptModule',
  components: {
    InteractiveModulePrompt,
    TEIError,
    StudioTextEditor,
  },
  mixins: [moduleData, teiValidation],
  props: {
    active: Boolean,
    canvasName: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true, // used in the moduleData mixin
    },
    mode: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    scale: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    ...mapState(useAssessmentStore, [
      'attemptItemByTeiId',
      'userCanEditAttemptQuizItems',
    ]),
    answer() {
      return get(this.attemptItemByTeiId(this.teiId), 'try.response.answerFieldData[0]');
    },
    draftStatus() {
      return this.$route && this.$route.name === 'edit' ? 'draft' : 'creation';
    },
    teiId() {
      if (!this.moduleData.references) return null;

      const teiReference = get(this, 'moduleData.references', [])
        .find((ref) => ref.reference_type === 'asset');
      return teiReference && teiReference.reference_id;
    },
  },
  methods: {
    ...mapActions(useAssessmentStore, [
      types.UPDATE_RESPONSE,
    ]),
    decodeHTML,
    submitAnswer: debounce(function updateAnswer({ html }) {
      this[types.UPDATE_RESPONSE]({
        teiId: this.teiId,
        payload: {
          answerFieldData: [html],
        },
      });
    }, 1000),
  },
};
</script>

<style lang="stylus">
.short-answer {
  background-color: $nebula-color-white;
  padding: $nebula-space-2x;

  &__instructions {
    color: $comet-color-neutral-50;
    font-size: $nebula-font-size-caption;
    font-weight: bold;
    margin: 0 0 $nebula-space-1x 0;

    &--view-mode {
      margin: 0 0 $nebula-space-2x 0;
    }
  }

  &__prompt {
    margin: 0 0 $nebula-space-2x 0;
  }

  &__text-editor { // .short-answer__text-editor
    border-radius: $nebula-border-radius-badge-default;
    box-shadow: 0px 0px 0px 1px $comet-color-neutral-80;
    margin: 0 0 $nebula-space-1x 0;
    min-height: $nebula-space-8x;
    width: 100%;
  }

  &__textarea { // .short-answer__textarea
    height: 100px;
    min-height: 0;
  }
}
</style>
